// gameModals.js
import { createMultiStepModal } from './multiStepModal.js';
import { createImageSelectModal } from './imageSelectModal.js';
import { createTranslateModal } from './translateModal.js';
import { createElement, icons } from 'lucide';
import { ClassicEditor } from 'ckeditor5';
import {
  showBoardChoiceModal,
  getSelectedBoardVersion,
  applyBoardConfiguration,
} from '@/scripts/UI/boardChoiceModal.js';
import { dropdownLanguages } from '@/scripts/header/appHeader.js';

function getSelectedLanguageCode() {
  try {
    const stored = localStorage.getItem('language');
    if (stored) return JSON.parse(stored)[0][0];
  } catch {
    // malformed/legacy value — fall back to English below
  }
  return 'eng';
}

export function setupGameModals({
  currLang,
  importImgVar,
  sendToDatabase,
  mode,
  curPage,
  editorConfig,
  showErrorMsg,
  updateToolboxTranslations, // NEW
  onCodeButtonClick,
  workspace, // NEW — Blockly workspace, only meaningful on vernatics_esp
}) {
  // one shared gallery, reused by both save and publish
  const imageSelectModal = createImageSelectModal({
    importImgVar,
    headingText: currLang['ImgSelectModal.HeadingText'],
    submitLabel: currLang['Game.SubmitText'],
  });
  const translateModal = createTranslateModal({
    currLang,
    curPage,
    updateToolboxTranslations,
  });
  function submitToDatabase(action, title, imgKey) {
    if (curPage.includes('sketch')) {
      sendToDatabase(action, title, imgKey, JSON.stringify(mode.p5jsData));
    } else if (curPage.includes('compiler')) {
      sendToDatabase(action, title, imgKey, JSON.stringify(mode.compilerData));
    } else {
      sendToDatabase(action, title, imgKey);
    }
  }

  // gameModals.js — updated buildSaveOrPublishStep
  function buildSaveOrPublishStep(ctx, stepName, { heading, titleId, submitLabel, action }) {
    const wrapper = document.createElement('div');
    wrapper.className = 'form-modal';
    const selectedImage = ctx.data.selectedImage ?? 'imgactivity1';
    const savedTitle = ctx.data.title ?? '';

    wrapper.innerHTML = `
    <p class="modal-heading-text">${heading}</p>
    <div class="form-modal__field">
      <label class="form-modal__label" for="${titleId}">${currLang['SaveModal.Label1']}</label>
      <input class="form-modal__input" type="text" id="${titleId}"
             placeholder="${currLang['SaveModal.PlaceHolder1.Text']}" value="${savedTitle}">
    </div>
    <div class="form-modal__image-picker">
      <img src="${importImgVar(selectedImage)}">
      <span class="form-modal__image-picker__label">${currLang['UI.ChangeImage']}</span>
    </div>
  `;

    const titleInput = wrapper.querySelector(`#${titleId}`);

    wrapper.querySelector('.form-modal__image-picker').addEventListener('click', () => {
      ctx.data.title = titleInput.value;
      imageSelectModal.open((key) => ctx.goTo(stepName, { selectedImage: key }));
    });

    const submitBtn = document.createElement('button');
    submitBtn.type = 'button';
    submitBtn.className = 'modal-btn modal-btn--primary';
    submitBtn.textContent = submitLabel;
    submitBtn.addEventListener('click', () => {
      const title = titleInput.value;
      if (!title) {
        showErrorMsg(currLang['ErrorText.EmptyInput'], null, false);
        return;
      }
      submitToDatabase(action, title, ctx.data.selectedImage ?? 'imgactivity1');
      ctx.close();
    });
    wrapper.appendChild(submitBtn);

    return wrapper;
  }
  async function handleBoardSwitch(ctx) {
    ctx.close();
    try {
      const newVersion = await showBoardChoiceModal(); // sets selected version internally
      await applyBoardConfiguration(newVersion, workspace);

      // keep ?level= in the URL in sync so a page refresh doesn't
      // silently fall back to a stale board
      const url = new URL(window.location.href);
      url.searchParams.set('level', newVersion);
      window.history.replaceState({}, '', url);
    } catch {
      // user cancelled the board picker — no-op
    }
  }

  const moreModal = createMultiStepModal(
    {
      menu: (ctx) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'more-modal__menu';

        const makeBtn = (label, icon, variant, onClick) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = `more-modal__btn more-modal__btn--${variant}`;

          const iconBadge = document.createElement('span');
          iconBadge.className = 'more-modal__icon';
          iconBadge.appendChild(createElement(icon, { width: 18, height: 18 }));

          const labelEl = document.createElement('span');
          labelEl.className = 'more-modal__label';
          labelEl.textContent = label;

          const chevron = document.createElement('span');
          chevron.className = 'more-modal__chevron';
          chevron.appendChild(createElement(icons.ChevronRight, { width: 18, height: 18 }));

          btn.append(iconBadge, labelEl, chevron);
          btn.addEventListener('click', onClick);
          wrapper.appendChild(btn);
        };

        makeBtn(currLang['Game.ShareButtonText'], icons.Share2, 'share', () => ctx.goTo('share'));
        makeBtn(currLang['SaveButton.Text'], icons.Save, 'save', () => ctx.goTo('save'));
        makeBtn(currLang['PublishButton.Text'], icons.Rocket, 'publish', () => ctx.goTo('publish'));

        // Lets the user switch UI language from here too, for pages/contexts
        // where the app header's own language picker (top-right avatar menu)
        // is hidden — see hidePageElements()/checkNavActive() in shareFeature.js.
        const currentLangEntry = dropdownLanguages.find(
          ([, code]) => code === getSelectedLanguageCode()
        );
        makeBtn(
          `${currLang['LanguageSwitch.ButtonText']}${
            currentLangEntry ? ` (${currentLangEntry[0]})` : ''
          }`,
          icons.Globe,
          'language',
          () => ctx.goTo('language')
        );

        // NEW — only relevant on the ESP page where boards/pins exist at all
        if (curPage.includes('vernatics_esp')) {
          const currentVersion = getSelectedBoardVersion();
          const boardLabel =
            currentVersion === 'v2'
              ? currLang['UI.BoardV2'].toUpperCase()
              : currLang['UI.BoardV1'].toUpperCase();
          makeBtn(
            `${currLang['BoardSwitch.ButtonText']} (${boardLabel})`,
            icons.Settings, // was icons.Cpu
            'board',
            () => handleBoardSwitch(ctx)
          );
        }

        if (location.href.includes('factory')) {
          makeBtn(currLang['CodeButton.Text'], icons.Code2, 'code', () => {
            ctx.close();
            (onCodeButtonClick ?? translateModal.open)();
          });
        }

        return wrapper;
      },

      share: (ctx) => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = `
          <h2>${currLang['ShareFeature.HowToShare']}</h2>
          <div id="editorContainer" style="display:none;"><div id="editor"></div></div>
        `;

        const buttons = document.createElement('div');
        buttons.className = 'button-container';

        const addInstructionsBtn = document.createElement('button');
        addInstructionsBtn.className = 'share share-button';
        addInstructionsBtn.textContent = currLang['ShareFeature.AddInstructions'];

        const directShareBtn = document.createElement('button');
        directShareBtn.className = 'share share-button';
        directShareBtn.textContent = currLang['ShareFeature.ShareWithoutInstructions'];

        const submitBtn = document.createElement('button');
        submitBtn.className = 'share share-button';
        submitBtn.textContent = currLang['Game.ShareButtonText'];
        submitBtn.style.display = 'none';

        let editor = null;

        addInstructionsBtn.addEventListener('click', () => {
          wrapper.querySelector('#editorContainer').style.display = 'block';
          addInstructionsBtn.style.display = 'none';
          directShareBtn.style.display = 'none';
          submitBtn.style.display = 'inline-block';
          ClassicEditor.create(wrapper.querySelector('#editor'), editorConfig)
            .then((e) => (editor = e))
            .catch(console.error);
        });

        directShareBtn.addEventListener('click', () => {
          sendToDatabase('share');
          ctx.close();
        });

        submitBtn.addEventListener('click', () => {
          sendToDatabase('share', null, null, null, editor?.getData() ?? '');
          ctx.close();
        });

        buttons.append(addInstructionsBtn, directShareBtn, submitBtn);
        wrapper.appendChild(buttons);
        return wrapper;
      },

      save: (ctx) =>
        buildSaveOrPublishStep(ctx, 'save', {
          heading: currLang['SaveModal.HeadingText'],
          titleId: 'save-title-input',
          submitLabel: currLang['Game.SubmitText'],
          action: 'save',
        }),

      publish: (ctx) =>
        buildSaveOrPublishStep(ctx, 'publish', {
          heading: currLang['PublishModal.HeadingText'],
          titleId: 'publish-title-input',
          submitLabel: currLang['Game.SubmitText'],
          action: 'publish',
        }),

      language: () => {
        const wrapper = document.createElement('div');
        wrapper.className = 'language-modal';

        const heading = document.createElement('p');
        heading.className = 'modal-heading-text';
        heading.textContent = currLang['Language.Text'];
        wrapper.appendChild(heading);

        const list = document.createElement('div');
        list.className = 'language-modal__options';

        const currentCode = getSelectedLanguageCode();

        dropdownLanguages.forEach(([label, code]) => {
          const optBtn = document.createElement('button');
          optBtn.type = 'button';
          optBtn.className = 'language-modal__option';
          if (code === currentCode) optBtn.classList.add('language-modal__option--active');
          optBtn.textContent = label;
          optBtn.addEventListener('click', () => {
            if (code === currentCode) return;
            localStorage.setItem('language', JSON.stringify([[code]]));
            location.reload();
          });
          list.appendChild(optBtn);
        });

        wrapper.appendChild(list);
        return wrapper;
      },
    },
    { initialStep: 'menu', size: 'md' }
  );

  return moreModal;
}
